/** @module FormulatedProduct/FormulatedProductSKUForm */
import config from "csi/config";
import declare from "dojo/_base/declare";
import crews from "csi/access/crews";
import def from "csi/def";
import Action from "csi/def/Action";
import ViewRef from "csi/def/ViewRef";
import FieldConstructor, { Field } from "csi/def/Field";
import lang from "dojo/_base/lang";
import Relationship from "csi/def/Relationship";
import { sessionStore } from "csi/state/rootStore";
import Cache from "csi/store/Cache";
import { StoreNames } from "csi/store/Names";
import Widget from "csi/form/Widget";
import ErrorText from "csi/widget/ErrorText";
import xml from "csi/xml";
import locale from "csi/locale";
import { View } from "csi/def/View";
import resolver from "csi/resolver";
import html from "csi/html";

const AUTOGTIN = "AutoGTIN";
const BLANK = "";
const COPY_COUNT = "CopyCount";
const PRODUCT_SALES_REGION = "FPSProductSalesRegion";

/* Projects */
const PROJECT = "Project";
const PROJECT_CREATE_ROLES = ["Project Administrator", "Project Editor"];
const NONE = "ProjectLineItemToSKU:None";
const PROJECT_LINE_ITEM_TO_SKU_ONE_FOR_ALL_SKU_REQUIRED = "ProjectLineItemToSKU:OneForAllSKURequired";
const PROJECT_LINE_ITEM_TO_SKU_ONE_FOR_EACH_SKU_REQUIRED = "ProjectLineItemToSKU:OneForEachSKURequired";

const FormulatedProductSKUForm = declare(Widget, {
	/** @property {Field} projectField - Project field */
	projectField: null,

	/**
	 * @override
	 */
	_buildFields(fields: Array<Field>): void {
		this.inherited(arguments);

		const action = this.getSourceController().getAction()
			.getId();

		this.setFieldValue("CopyCount", 1);

		// Hide CopyCount if is New action
		if (this.isNew()) {
			this.setFieldHidden("CopyCount", true);
		}

		if (this.isEdit()) {
			this.setFieldHidden(this.projectField, true);
		}

		// This call is put here, because we need access to m_control attribute to setFieldValue (check in Widget.js)
		if (this.isCopy()) {
			this.initializeProjectFieldForSKUCopy();
		}

		// If NewFromFoodSKU, make the sales region value blank
		if (action == "NewFromFoodSKU") {
			this.hideDataSheetFields();
			this.setFieldValue("FPSProductSalesRegion", BLANK);
		}
	},

	/**
	 * C8FB-1717
	 * Hide datasheets on form for new from foodSKU
	 */
	hideDataSheetFields() {
		this.setFieldHidden("SKUBOMs", true);
		this.setFieldHidden("SKUFoodBOM", true);
		this.setFieldHidden("SKUFoodLabel", true);
		this.setFieldHidden("SKULabelDataSheets", true);
		this.setFieldHidden("SKURecipe", true);
		this.setFieldHidden("SKURecipes", true);
		this.setFieldHidden("SKUSpecs", true);
		this.setFieldHidden("SKUArtworks", true);
		this.setFieldHidden("SKUTestRuns", true);
	},

	/**
	 * @override
	 * Form validation to check copyCount field value is too large
	 */
	validateForm(errorParams: Object): boolean {
		const numToCopyField = this.getField("CopyCount");
		const createCap = config.getSubtypeSetting(this._getParentOfClientNode()?.Subtype, "MultiCopySKULimit") as unknown as number;
		if (numToCopyField) {
			const fieldValue = numToCopyField.getValue() as unknown as number;
			if (fieldValue > createCap) {
				return false;
			}
		}
		return this.inherited<boolean>(arguments);
	},

	/**
	 * Make sure correct amount of copies are selected and error if not
	 */
	_onValidateCopyCount(formView: View, field2: Field, value: string, params: Object): void {
		const copyCountField = this.getField("CopyCount");
		if (copyCountField && this._getParentOfClientNode()) {
			const fieldValue = Number(copyCountField.getValue());
			const copyLimit = config.getSubtypeSetting(this._getParentOfClientNode()?.Subtype, "MultiCopySKULimit") as unknown as number;
			if (fieldValue > copyLimit) {
				throw new Error(locale.resource("FoodSKUMassCreateError_TooMany", [copyLimit]));
			}
			if (!value || Number.isNaN(Number(value)) || !Number.isInteger(fieldValue) || fieldValue < 1 || value.includes(".")) {
				throw new Error(locale.resource("FoodSKUMultiCopy_Invalid"));
			}
		}
	},

	/**
	 * @override
	 */
	_resolveView(): void {
		// Add FPSProductSalesRegion field
		this.insertFieldIfNotExist(PRODUCT_SALES_REGION, 1, {
			m_typeControl: def.FIELD_FILTERING_SELECT,
		});

		// Add AutoGTIN checkbox for New and Copy form
		if (this.isNew() || this.isCopy()) {
			if (xml.isValidURL(sessionStore.siteCompany.DefaultManufacturerCode)) {
				this.insertField(lang.mixin(new FieldConstructor(), {
					m_label: AUTOGTIN,
					m_typeControl: def.FIELD_CHECKBOX,
					bPersist: true,
				}));
			}
		}

		// C8FB-1249: If parent Food subtype settings has ProjectLineItemToSKU != None then add Project field
		const projectLineItemToSKU = config.getSubtypeSetting(this._getParentOfClientNode()?.Subtype, "ProjectLineItemToSKU");

		const requiredProject: boolean =
			projectLineItemToSKU &&
			(projectLineItemToSKU === PROJECT_LINE_ITEM_TO_SKU_ONE_FOR_ALL_SKU_REQUIRED || projectLineItemToSKU === PROJECT_LINE_ITEM_TO_SKU_ONE_FOR_EACH_SKU_REQUIRED);

		if (projectLineItemToSKU && projectLineItemToSKU !== NONE) {
			this.projectField = this.insertField(lang.mixin(new FieldConstructor(), {
				m_id: PROJECT,
				m_label: locale.type(PROJECT),
				m_bRequired: requiredProject,
				m_typeControl: def.FIELD_FILTERING_SELECT,
				m_onValidate: "_onChangeProject",
			}), 2);
		}

		// C8FB-1528: Field does not exist without DevelopmentConfig.js in generated folder
		// Custom Configured Attributes do not appear with DevelopmentConfig.js in generated folder
		const multiCopySKULimit = config.getSubtypeSetting(this._getParentOfClientNode()?.Subtype, "MultiCopySKULimit") as unknown as number;
		const _copyFieldText = declare(ErrorText, {
			infoResource: "QuantityField_CopyForm_InfoText_FoodSKU",
		});
		if (this.isCopy() && multiCopySKULimit > 1) {
			this.insertFieldIfNotExist(COPY_COUNT, 3, {
				m_onValidate: "_onValidateCopyCount",
				messageClass: _copyFieldText,
			});
		}

		this.inherited(arguments);
	},

	/**
	 * Add the Required style to the COPY_COUNT field
	 * @override
	 * @param tr
	 * @param bRequired
	 * @returns
	 */
	_addSeparator(tr: HTMLElement, bRequired: boolean) {
		return html.create("td", {
			class: "csiFormViewSeparator",
			innerText: bRequired || tr.dataset.csiFormField === COPY_COUNT ? "*" : null,
		}, tr);
	},

	/**
	 * @override
	 */
	getFieldStore(field: Field, value: string): StoreNames {
		if (field.m_id === PROJECT) {
			return this._getProjectFieldStore(field);
		}
		return this.inherited<StoreNames>(arguments);
	},

	/**
	 * Create field store for Project field.
	 */
	_getProjectFieldStore(field: Field): StoreNames {
		const viewRefId = "ProjectsWithViewAccess";
		const viewRefURL = "centric://REFLECTION/INSTANCE/_CS_Data/null";
		const ctx = this.getContext();
		const rel = lang.mixin(new Relationship(), {
			m_typeNode: PROJECT,
			m_module: "SiteAdmin",
			m_operation: "GetProjectsWithViewAccess",
		});

		ctx.setNodeURL(this._getParentURLOfClientNode());

		const priorityStore = ctx.view.createHeadingNamesStore(
			ctx,
			field,
			null,
			lang.mixin(new Relationship(), {
				m_typeNode: PROJECT,
				m_module: "FormulatedProduct",
				m_operation: "GetProjectsForFood",
				m_url: this._getParentURLOfClientNode(),
				m_sorts: [{ id: xml.ATTR_NAME }],
			}),
		);

		const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel, null, {
			priorityStore: priorityStore,
			defaultToPriorityOnly: true,
		});

		store.action = lang.mixin(new Action(), {
			m_viewRef: lang.mixin(new ViewRef(), {
				m_id: viewRefId,
				m_url: viewRefURL,
			}),
		});

		return store;
	},

	/**
	 * Initialize "Project" field to original SKU's Project if there is only one.
	 */
	async initializeProjectFieldForSKUCopy(): Promise<void> {
		const originalSKU = this.getContext().getNode();
		if (originalSKU.SKUProjectLineItems?.length === 1) {
			const projectNode = resolver.resolveParent(originalSKU.SKUProjectLineItems[0]);
			this.setFieldValue(this.projectField, projectNode?.$URL);
		}
	},

	/**
	 * Handle validation of Project field on change of project
	 * @param {defView} view- view
	 * @param {field} field - field on the form
	 * @param {projectValue} string - value of the field
	 */
	_onChangeProject(defView, field, projectValue) {
		if (!this.projectField) {
			return;
		}

		// Check for global access
		if (crews.isInGlobalRoles(PROJECT_CREATE_ROLES)) {
			return;
		}
		// Check edit access for the specific project
		const cache = this.getCache() as Cache;
		const projectNode = cache.getNode(projectValue);
		if (crews.isInRoles(PROJECT_CREATE_ROLES, projectNode)) {
			return;
		} else {
			throw new Error(locale.resource("ProjectError_NotEditable"));
		}
	},
});

export = FormulatedProductSKUForm;
